import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Activity,
  Users,
  Briefcase,
  MessageSquare,
  Search,
  RefreshCw,
  Clock,
  ArrowLeft,
  Shield,
  Eye,
  FileText,
  UserCheck,
  Send,
  LogIn
} from "lucide-react";

const AdminActivity = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityType, setActivityType] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [userActivity, setUserActivity] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchActivities();
  }, [user, activityType]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activityType) params.append("activity_type", activityType);
      params.append("limit", "100");
      
      const res = await fetch(`${API}/admin/activity/recent?${params}`, { 
        credentials: "include" 
      });
      if (res.ok) {
        const data = await res.json();
        setActivities(data.activities || []);
      }
    } catch (error) {
      toast.error("Failed to fetch activities");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserActivity = async (userId) => {
    try {
      const res = await fetch(`${API}/admin/activity/user/${userId}`, {
        credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setUserActivity(data);
        setSelectedUserId(userId);
      }
    } catch (error) {
      toast.error("Failed to fetch user activity");
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "login": return <LogIn className="w-4 h-4 text-blue-400" />;
      case "application": return <FileText className="w-4 h-4 text-green-400" />;
      case "job_posted": return <Briefcase className="w-4 h-4 text-purple-400" />;
      case "message": return <Send className="w-4 h-4 text-cyan-400" />;
      case "interview": return <UserCheck className="w-4 h-4 text-yellow-400" />;
      default: return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-crimson flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">USER ACTIVITY</span>
            </div>
          </div>
          <Button
            onClick={fetchActivities}
            variant="outline"
            className="border-[#333] text-white"
            data-testid="refresh-activity-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <select
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
              className="px-4 py-2 bg-obsidian-light border border-[#333] text-white focus:border-crimson"
              data-testid="activity-type-filter"
            >
              <option value="">All Activities</option>
              <option value="login">Logins</option>
              <option value="application">Applications</option>
              <option value="job_posted">Job Postings</option>
              <option value="message">Messages</option>
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Activity Feed */}
            <div className="lg:col-span-2 bg-obsidian-light border border-[#222] p-6">
              <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-crimson" />
                Recent Activity Feed
              </h2>
              
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {activities.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No recent activity</p>
                ) : (
                  activities.map((activity, i) => (
                    <motion.div
                      key={`${activity.type}-${activity.timestamp}-${i}`}
                      className="flex items-start gap-4 p-4 bg-obsidian border border-[#333] hover:border-crimson/30 transition-colors cursor-pointer"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.02 }}
                      onClick={() => fetchUserActivity(activity.user_id)}
                      data-testid={`activity-${i}`}
                    >
                      <div className="w-10 h-10 bg-obsidian-light border border-[#333] flex items-center justify-center">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm">{activity.description}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-gray-500 text-xs">{formatTime(activity.timestamp)}</span>
                          {activity.user_role && (
                            <span className={`text-xs px-2 py-0.5 ${
                              activity.user_role === "recruiter" ? "bg-purple-500/20 text-purple-400" :
                              activity.user_role === "jobseeker" ? "bg-green-500/20 text-green-400" :
                              "bg-crimson/20 text-crimson"
                            }`}>
                              {activity.user_role}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>

            {/* User Detail Panel */}
            <div className="bg-obsidian-light border border-[#222] p-6">
              <h2 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-crimson" />
                User Details
              </h2>
              
              {userActivity ? (
                <div className="space-y-4">
                  <div className="p-4 bg-obsidian border border-[#333]">
                    <h3 className="font-bold text-white">{userActivity.user?.name || "Unknown"}</h3>
                    <p className="text-gray-400 text-sm">{userActivity.user?.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-xs px-2 py-0.5 ${
                        userActivity.user?.role === "recruiter" ? "bg-purple-500/20 text-purple-400" :
                        userActivity.user?.role === "jobseeker" ? "bg-green-500/20 text-green-400" :
                        "bg-crimson/20 text-crimson"
                      }`}>
                        {userActivity.user?.role}
                      </span>
                      <span className="text-xs text-gray-500">
                        {userActivity.user?.subscription_tier}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-gray-400">Recent Activity</h4>
                    {userActivity.activities?.slice(0, 10).map((act, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-obsidian border border-[#333] text-sm">
                        {getActivityIcon(act.type)}
                        <span className="text-gray-300 capitalize">{act.type.replace("_", " ")}</span>
                        <span className="text-gray-500 text-xs ml-auto">{formatTime(act.timestamp)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">Click on an activity to view user details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminActivity;
